/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-01-26 20:29:20
 */
import React, { useState, useRef, useEffect } from 'react'
import { Button, Divider, Dropdown } from 'antd'
import dayjs from 'dayjs'
import {
  CalendarOutlined,
  CaretDownOutlined,
  RightOutlined
} from '@ant-design/icons'
import {
  setFilter,
  setQueryFilter,
  fetchPhotos,
  resetFilter,
  setLastPageCond,
  fetchWatermarkList
} from '../../../../../actions/photo'
import { reset as resetPhotosByLocation } from '../../../../../slices/photosByLocationSlice'
import { reset as resetPhotosByPrecise } from '../../../../../slices/photosByPreciseSlice'
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks'
import WatermarksPicker from '../../../../components/WatermarksPicker'
import LocationPicker from '../../../../components/LocationPicker'
import DatesPicker from '../../../../components/DatesPickerV2'
import Keywords from '../../../../components/Keywords'
import DepartmentMemberPickerRaw from '../../../../components/DepartmentMemberPickerV2'
const DepartmentMemberPicker = DepartmentMemberPickerRaw as any
import { buildFetchPhotosParamsWithOptions } from '@/pages/components/photoLib/photo'
import { capturePromiseException } from '../../../../../utils/sentryUtils'
import actionTypes from '../../../../../constants/actionTypes'
import { setLoading, fetchConfig } from '../../../../../actions/main'
import { setIsAscSort as setContentIsAscSort } from '../../../../../slices/photosByContentSlice'
import { setIsAscSort as setPreciseIsAscSort } from '../../../../../slices/photosByPreciseSlice'
import { useAddPhotosTrack, formatDate } from '../hooks/useAddPhotosTrack'
import CONFIG from '../../../../../constants/config'
import './Filter.scss'

interface FilterProps {
  sameWatermarkOnly?: boolean
}

const Filter: React.FC<FilterProps> = ({ sameWatermarkOnly = false }) => {
  const dispatch = useAppDispatch()
  const filter = useAppSelector((state) => state.photo.filter)
  const initialFilterRef = useRef(filter)
  const queryFilter = useAppSelector((state) => state.photo.queryFilter)
  const groupId = useAppSelector((state) => state.group.groupId)
  const allWatermarksNum = useAppSelector(
    (state) => state.photo.allWatermarksNum
  )
  const pickerUserCount = useAppSelector(
    (state) => state.organization.pickerOrgUserCount
  )

  const fetchWatermarkListHandler = (params: object) =>
    dispatch(fetchWatermarkList(params))

  const [showDatePickerPane, setShowDatePickerPane] = useState(false)
  const [showMemberPickerModal, setShowMemberPickerModal] = useState(false)
  const [showWatermarkModal, setShowWatermarkModal] = useState(false)
  const [showLocationPickerModal, setShowLocationPickerModal] = useState(false)
  const [webWatermarkSwitch, setWebWatermarkSwitch] = useState(false)
  const moreFilterDropdown = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (sameWatermarkOnly && showWatermarkModal) {
      setShowWatermarkModal(false)
    }
  }, [sameWatermarkOnly, showWatermarkModal])

  // 获取水印筛选展示KV
  useEffect(() => {
    const getWebWatermarkSwitch = async () => {
      try {
        const res = await dispatch(
          fetchConfig({
            names: CONFIG.KVParams.names
          })
        )

        const watermarkConfig = res.items?.find(
          (item: any) => item.name === 'web_watermark_switch'
        )

        const switchValue = watermarkConfig
          ? watermarkConfig.value === 'true' || watermarkConfig.value === true
          : false

        setWebWatermarkSwitch(switchValue)
      } catch (error) {
        console.error('获取水印配置失败:', error)
        setWebWatermarkSwitch(false)
      }
    }

    getWebWatermarkSwitch()
  }, [dispatch])

  // 使用埋点 hook
  const { trackAddPhotos } = useAddPhotosTrack()

  // 处理模态框显示隐藏
  const handleFilterModals = (
    type: 'time' | 'member' | 'watermark' | 'location'
  ) => {
    if (type === 'time') {
      setShowDatePickerPane(!showDatePickerPane)
      setShowMemberPickerModal(false)
      setShowWatermarkModal(false)
      setShowLocationPickerModal(false)
    } else if (type === 'member') {
      setShowMemberPickerModal(!showMemberPickerModal)
      setShowDatePickerPane(false)
      setShowWatermarkModal(false)
      setShowLocationPickerModal(false)
    } else if (type === 'watermark') {
      setShowWatermarkModal(!showWatermarkModal)
      setShowDatePickerPane(false)
      setShowMemberPickerModal(false)
      setShowLocationPickerModal(false)
    } else if (type === 'location') {
      setShowLocationPickerModal(!showLocationPickerModal)
      setShowDatePickerPane(false)
      setShowMemberPickerModal(false)
      setShowWatermarkModal(false)
    }
  }

  // 获取照片列表
  const handleFetchPhotos = (isAscSort: boolean, overrideFilter?: any) => {
    dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
    dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
    dispatch({ type: actionTypes.SET_CHECK_ALL, data: false })
    dispatch(resetPhotosByLocation())
    dispatch(resetPhotosByPrecise())

    const finalIsAscSort = isAscSort || false

    dispatch(setContentIsAscSort(finalIsAscSort))
    dispatch(setPreciseIsAscSort(finalIsAscSort))

    dispatch(setLoading(true))

    const promises: Promise<any>[] = []

    promises[0] = dispatch(
      fetchPhotos(
        buildFetchPhotosParamsWithOptions({
          queryFilter: overrideFilter || (queryFilter ? queryFilter : filter),
          groupID: groupId,
          isAscSort: finalIsAscSort,
          lastPageCond: ''
        })
      )
    )
      .then((result: any) => {
        if (result.payload && result.payload.expandInfo) {
          dispatch({
            type: actionTypes.SET_PHOTOS_EXPAND_INFO,
            data: result.payload.expandInfo
          })
        }
        return result
      })
      .catch(capturePromiseException)

    Promise.all(promises).then(() => {
      dispatch(setLoading(false))
    })
  }

  // 触发筛选的通用函数
  const triggerFilter = (newFilter: any) => {
    dispatch(setLastPageCond(''))

    const finalFilter = {
      ...newFilter,
      suggestedSearchWords:
        newFilter.searchKeywords.length !== 1
          ? []
          : newFilter.suggestedSearchWords,
      useSegment: true
    }

    dispatch(setFilter(finalFilter))

    dispatch(setContentIsAscSort(false))
    dispatch(setPreciseIsAscSort(false))

    dispatch(setQueryFilter(finalFilter)).then(() => {
      handleFetchPhotos(false, finalFilter)
    })
  }

  // 时间选择处理
  const handleDatesChange = (startDate: any, endDate: any) => {
    const searchKeywords = filter.searchKeywords
    trackAddPhotos({
      actionType: 'clickPhotoTime',
      start_date: formatDate(startDate),
      end_date: formatDate(endDate),
      searchWord:
        searchKeywords && searchKeywords.length > 0
          ? searchKeywords[0] || ''
          : ''
    })

    setShowDatePickerPane(false)
    const newTimes: [any, any] = startDate.isAfter(endDate)
      ? [endDate, startDate]
      : [startDate, endDate]
    const newFilter = { ...filter, times: newTimes }
    triggerFilter(newFilter)
  }

  // 水印选择处理
  const handleWatermarksChange = (checkedList: string[]) => {
    const searchKeywords = filter.searchKeywords
    trackAddPhotos({
      actionType: 'clickWatermar',
      show_watermark_list: checkedList,
      searchWord:
        searchKeywords && searchKeywords.length > 0
          ? searchKeywords[0] || ''
          : ''
    })

    setShowWatermarkModal(false)
    const newFilter = { ...filter, watermarks: checkedList }
    triggerFilter(newFilter)
  }

  // 成员选择处理
  const handleMemberChangeV2 = (params: {
    selectedDepartmentUsers: any[]
    selectedDepartments: any[]
  }) => {
    const searchKeywords = filter.searchKeywords
    trackAddPhotos({
      actionType: 'clickPhotoMember',
      searchWord:
        searchKeywords && searchKeywords.length > 0
          ? searchKeywords[0] || ''
          : ''
    })

    setShowMemberPickerModal(false)
    const { selectedDepartmentUsers, selectedDepartments } = params
    const newFilter = {
      ...filter,
      departments: selectedDepartments,
      departmentUsers: selectedDepartmentUsers
    }
    triggerFilter(newFilter)
  }

  // 地点选择处理
  const handleLocationChange = (location: any) => {
    const searchKeywords = filter.searchKeywords
    trackAddPhotos({
      actionType: 'clickPhotoPlace',
      locationName: location?.name || '不限',
      searchWord:
        searchKeywords && searchKeywords.length > 0
          ? searchKeywords[0] || ''
          : ''
    })

    setShowLocationPickerModal(false)
    const newFilter = { ...filter, locations: [location] }
    triggerFilter(newFilter)
  }

  // 重置处理
  const handleReset = () => {
    const searchKeywords = filter.searchKeywords
    trackAddPhotos({
      actionType: 'clickReset',
      searchWord:
        searchKeywords && searchKeywords.length > 0
          ? searchKeywords[0] || ''
          : ''
    })

    dispatch(setLastPageCond(''))
    dispatch(resetFilter()).then(() => {
      handleFetchPhotos(false, initialFilterRef.current)
    })
    dispatch(resetPhotosByLocation())
    dispatch(resetPhotosByPrecise())
  }

  // 提交筛选
  const handleSubmit = () => {
    const startTime = filter.times[0]
    const endTime = filter.times[1]
    const locations = filter.locations
    const watermarks = filter.watermarks
    const searchKeywords = filter.searchKeywords

    trackAddPhotos({
      actionType: 'clickFilter',
      start_date: formatDate(startTime ? startTime : null),
      end_date: formatDate(endTime ? endTime : null),
      locationName:
        locations && locations.length > 0
          ? locations[0]?.name || '不限'
          : '不限',
      show_watermark_list:
        watermarks && watermarks.length > 0 ? watermarks : [],
      searchWord:
        searchKeywords && searchKeywords.length > 0
          ? searchKeywords[0] || ''
          : ''
    })

    setShowWatermarkModal(false)
    setShowLocationPickerModal(false)
    triggerFilter(filter)
  }

  // 渲染时间选择区域
  const renderTimePickerArea = () => {
    const startTime = filter.times[0]
    const endTime = filter.times[1]
    const isThisMonth =
      dayjs().startOf('month').isSame(startTime, 'second') &&
      dayjs().endOf('day').isSame(endTime, 'second')
    const isLastSixMonths =
      dayjs().subtract(6, 'months').startOf('day').isSame(startTime, 'day') &&
      dayjs().endOf('day').isSame(endTime, 'day')
    const startTimeFormated = dayjs().isSame(startTime, 'year')
      ? startTime.format('M月D日')
      : startTime.format('YY年M月D日')
    const endTimeFormated = dayjs().isSame(endTime, 'year')
      ? endTime.format('M月D日')
      : endTime.format('YY年M月D日')

    return (
      <div className="time-wrapper">
        <p className="label">筛选时间</p>
        <p
          className="timepicker-wrapper"
          onClick={() => {
            const searchKeywords = filter.searchKeywords
            trackAddPhotos({
              actionType: 'clickPhotoTime',
              searchWord:
                searchKeywords && searchKeywords.length > 0
                  ? searchKeywords[0] || ''
                  : ''
            })
            handleFilterModals('time')
          }}
        >
          {isThisMonth && <span className="this-month">本月 丨 </span>}
          {isLastSixMonths ? (
            <span className="this-month">过去6个月</span>
          ) : (
            startTimeFormated + '-' + endTimeFormated
          )}
          <CalendarOutlined />
        </p>
        <p className="quickpicker-wrapper">
          快速筛选{' '}
          {[
            {
              text: '今天',
              startTime: () => dayjs().startOf('day'),
              endTime: () => dayjs().endOf('day')
            },
            {
              text: '昨天',
              startTime: () => dayjs().add(-1, 'day').startOf('day'),
              endTime: () => dayjs().add(-1, 'day').endOf('day')
            },
            {
              text: '过去30天',
              startTime: () => dayjs().add(-30, 'days').startOf('days'),
              endTime: () => dayjs().add(-1, 'days').endOf('days')
            },
            {
              text: '本月',
              startTime: () => dayjs().startOf('month'),
              endTime: () => dayjs().endOf('day')
            },
            {
              text: '过去6个月',
              startTime: () => dayjs().subtract(6, 'months').startOf('day'),
              endTime: () => dayjs().endOf('day')
            }
          ].map((item, index) => (
            <Button
              type="link"
              size="small"
              onClick={() => {
                handleDatesChange(item.startTime(), item.endTime())
              }}
              key={index}
            >
              {item.text}
            </Button>
          ))}
        </p>
        <DatesPicker
          isAllPhoto={true}
          visible={showDatePickerPane}
          dates={filter.times}
          onChange={handleDatesChange}
          onCancel={() => setShowDatePickerPane(false)}
        />
      </div>
    )
  }

  // 计算水印显示文案
  const handleCalcWatermarkPickedValue = () => {
    const watermarks = filter.watermarks
    if (
      !watermarks ||
      watermarks.length === 0 ||
      watermarks.length === allWatermarksNum
    ) {
      return `全部(${allWatermarksNum}个)`
    }
    return `${watermarks.length}个水印`
  }

  // 计算成员数量
  const handleCalcPickedMemberNum = () => {
    const departments = filter.departments
    const userNumInDepartments = departments
      .map((department: any) => department.num || 0)
      .reduce((acc: number, cur: number) => acc + cur, 0)
    const total = filter.departmentUsers.length + userNumInDepartments
    const hasSelection =
      filter.departmentUsers.length > 0 || departments.length > 0
    if (
      (!hasSelection && total === 0) ||
      total === pickerUserCount ||
      departments.some((department: any) => department.id === '0')
    ) {
      return pickerUserCount
    } else {
      return total
    }
  }

  // 计算成员显示文案
  const handleCalcMemberPickedValue = () => {
    const pickedMembersNum = handleCalcPickedMemberNum()
    if (pickedMembersNum === pickerUserCount) {
      return '全部成员'
    }
    return `已选${pickedMembersNum}人`
  }

  // 计算地点显示文案
  const handleCalcLocationPickedValue = () => {
    if (filter.locations.length > 0) {
      return filter.locations[0]?.name
    }
    return '不限'
  }

  // 计算更多筛选条件数量
  const handleFilterMoreCount = () => {
    let count = 0
    if (filter.locations.length > 0) {
      count++
    }
    if (filter.mediaType !== null) {
      count++
    }
    if (filter.existWatermark !== 0) {
      count++
    }
    return count
  }

  // 获取含水印筛选的显示文本
  const getExistWatermarkText = () => {
    const existWatermark = filter.existWatermark
    if (existWatermark === 0) return '不限'
    if (existWatermark === 1) return '含水印'
    if (existWatermark === 2) return '无水印'
    return '不限'
  }

  // 渲染关键词搜索区域
  const renderKeywordsArea = () => {
    return (
      <Keywords
        value={filter.searchKeywords || []}
        onChange={(value: any) => {
          dispatch(setFilter({ ...filter, searchKeywords: value }))
        }}
        onSensorOfClickEvent={() => {
          // 埋点逻辑可以在这里添加
        }}
        onSubmit={handleSubmit}
      />
    )
  }

  return (
    <div className="photo-filter-wrapper">
      <div className="time-and-keywords-wrapper">
        {renderTimePickerArea()}
        <Divider
          orientation="vertical"
          style={{ height: '52px', margin: '8px 17px' }}
        />
        {renderKeywordsArea()}
      </div>
      <div className="other-filter-wrapper" ref={moreFilterDropdown}>
        {/* 成员筛选 */}
        <div
          className="member-filter-dropdown"
          onClick={() => {
            const searchKeywords = filter.searchKeywords
            trackAddPhotos({
              actionType: 'clickPhotoMember',
              searchWord:
                searchKeywords && searchKeywords.length > 0
                  ? searchKeywords[0] || ''
                  : ''
            })
            handleFilterModals('member')
          }}
        >
          <span className="label">筛选人员</span>
          <span className="value">{handleCalcMemberPickedValue()}</span>
          <CaretDownOutlined />
        </div>
        <Divider
          orientation="vertical"
          style={{ height: '26px', margin: '0 16px' }}
        />
        {/* 水印筛选 */}
        <div
          className={`member-filter-dropdown ${
            sameWatermarkOnly ? 'is-disabled' : ''
          }`}
          onClick={() => {
            if (sameWatermarkOnly) {
              return
            }
            const searchKeywords = filter.searchKeywords
            trackAddPhotos({
              actionType: 'clickWatermar',
              searchWord:
                searchKeywords && searchKeywords.length > 0
                  ? searchKeywords[0] || ''
                  : ''
            })
            handleFilterModals('watermark')
          }}
        >
          <span className="label">筛选水印</span>
          <span className="value">{handleCalcWatermarkPickedValue()}</span>
          <CaretDownOutlined />
        </div>
        <Divider
          orientation="vertical"
          style={{ height: '26px', margin: '0 16px' }}
        />
        {/* 更多筛选下拉菜单 */}
        <Dropdown
          menu={{
            items: [
              {
                key: 'location',
                label: (
                  <span>
                    筛选地点：
                    {filter.locations.length > 0 ? '已选择' : '不限'}
                    <RightOutlined />
                  </span>
                ),
                onClick: () => handleFilterModals('location')
              },
              {
                key: 'mediaType',
                label: (
                  <span>
                    {`内容类型：${
                      filter.mediaType === null
                        ? '全部'
                        : filter.mediaType === 0
                          ? '仅照片'
                          : '仅视频'
                    }`}
                  </span>
                ),
                children: [
                  {
                    key: 'all',
                    label: '全部类型',
                    onClick: () =>
                      dispatch(setFilter({ ...filter, mediaType: null }))
                  },
                  {
                    key: 'photo',
                    label: '照片',
                    onClick: () =>
                      dispatch(setFilter({ ...filter, mediaType: 0 }))
                  },
                  {
                    key: 'video',
                    label: '视频',
                    onClick: () =>
                      dispatch(setFilter({ ...filter, mediaType: 1 }))
                  }
                ]
              },
              ...(webWatermarkSwitch
                ? ([
                    {
                      key: 'existWatermark',
                      label: (
                        <span>{`含水印：${getExistWatermarkText()}`}</span>
                      ),
                      children: [
                        {
                          key: 'nolimit',
                          label: '不限',
                          onClick: () =>
                            dispatch(
                              setFilter({ ...filter, existWatermark: 0 })
                            )
                        },
                        {
                          key: 'has',
                          label: '含水印',
                          onClick: () =>
                            dispatch(
                              setFilter({ ...filter, existWatermark: 1 })
                            )
                        },
                        {
                          key: 'no',
                          label: '无水印',
                          onClick: () =>
                            dispatch(
                              setFilter({ ...filter, existWatermark: 2 })
                            )
                        }
                      ]
                    }
                  ] as any[])
                : ([] as any[]))
            ] as any[]
          }}
          classNames={{ root: 'filter-more-menu' }}
          getPopupContainer={() => moreFilterDropdown.current || document.body}
        >
          <div
            className={`more-filter-dropdown ${
              handleFilterMoreCount() > 0 ? 'more-filtered' : ''
            }`}
          >
            <span className="label">
              更多筛选
              {handleFilterMoreCount() > 0 && `(${handleFilterMoreCount()})`}
            </span>
            <CaretDownOutlined />
          </div>
        </Dropdown>
        <DepartmentMemberPicker
          visible={showMemberPickerModal}
          filterObj={filter}
          onOk={handleMemberChangeV2}
          onCancel={() => setShowMemberPickerModal(false)}
        />
        <WatermarksPicker
          visible={showWatermarkModal}
          onOK={handleWatermarksChange}
          onCancel={() => setShowWatermarkModal(false)}
          checkedList={filter.watermarks}
          fetchWatermarkList={fetchWatermarkListHandler}
        />
        <LocationPicker
          visible={showLocationPickerModal}
          onCancel={() => setShowLocationPickerModal(false)}
          groupId={groupId}
          handleCheckLocation={handleLocationChange}
        />
      </div>
      <Button size="large" style={{ marginLeft: '20px' }} onClick={handleReset}>
        重置
      </Button>
      <Button
        size="large"
        type="primary"
        style={{
          width: '145px',
          marginLeft: '12px'
        }}
        onClick={handleSubmit}
      >
        筛选
      </Button>
    </div>
  )
}

export default Filter
